import { z } from "zod/v4";

/**
 * One rung a node put on the ladder: a value it was prepared to answer with,
 * and its own reason for that rung.
 */
const offered = <T extends z.ZodType>(value: T) =>
  z.object({ value, rationale: z.string() });

/**
 * One decision on a recommendation card, and everything that can have happened
 * to it.
 *
 * A card has a slot for every node in its test type's tree, filled or not, so
 * the card records the walk that was attempted rather than only the walk that
 * finished. Read across the fields it says what was tried, what was settled,
 * and where it stopped.
 *
 * The four states answer different questions, and collapsing any two of them
 * loses something a merchant would ask about:
 *
 * - `decided` — the node ran, offered a ladder, and one rung was taken. We know
 *   the answer.
 * - `rejected` — the node ran and offered a ladder, and every rung was wrong for
 *   this store: a 3-for-1 on a car dealer. We know there is no good option,
 *   which is not the same as not knowing. It keeps what it offered, because the
 *   rungs turned down are the most interesting part of that record.
 * - `failed` — the node ran and could not produce values at all. A read broke.
 *   We know nothing, and `rationale` is the whole reason.
 * - `notReached` — the node never ran, because something earlier stopped the
 *   walk. Nothing happened here, so there is nothing to say; the sentence lives
 *   on the field that stopped it, or on the card's own outcome.
 *
 * `rationale` is on the three arms that ran. All three matching ledger entries
 * carry one (`walk-ledger.ts`): the node's analysis joined with the walk's
 * reason when it decided, why every rung was wrong when it rejected, the whole
 * failure reason when it failed.
 */
export const decided = <T extends z.ZodType>(value: T) =>
  z.discriminatedUnion("type", [
    z.object({
      type: z.literal("decided"),
      value,
      rationale: z.string(),
      /**
       * The rungs this node offered and the walk did not take, in the node's
       * own order.
       *
       * **Real alternatives, not weaker guesses.** A node ranks a ladder and
       * every rung carries its own evidence and its own break-even. They were
       * dropped once already, by an agent re-authoring the decision list, and
       * the fix then was to pass the array through verbatim. Naming them here
       * is the same fix at the type level.
       */
      alternatives: z.array(offered(value)).default([]),
    }),
    z.object({
      type: z.literal("rejected"),
      rationale: z.string(),
      /** Every rung it offered, all of them turned down. */
      offered: z.array(offered(value)).default([]),
    }),
    z.object({ type: z.literal("failed"), rationale: z.string() }),
    z.object({ type: z.literal("notReached") }),
  ]);

/** One rung, as a type. */
export type Offered<T> = { value: T; rationale: string };

/**
 * The same union as a type, for code that holds a decision without a schema in
 * hand. Kept beside `decided` so the two cannot drift.
 */
export type Decided<T> =
  | {
      type: "decided";
      value: T;
      rationale: string;
      alternatives: Offered<T>[];
    }
  | { type: "rejected"; rationale: string; offered: Offered<T>[] }
  | { type: "failed"; rationale: string }
  | { type: "notReached" };

/**
 * Why a recommendation stopped, where no single field can say it.
 *
 * A node failing or rejecting is recorded on that node's own slot. A branch the
 * walk gave up on is different: no node failed, the walk simply decided the
 * path was not worth continuing, and every remaining slot is `notReached` with
 * nothing to point at. That reason is the branch's, so it lives here.
 */
export const AuditRecommendationOutcomeSchema = z.discriminatedUnion("type", [
  z.object({ type: z.literal("complete") }),
  z.object({ type: z.literal("abandoned"), rationale: z.string() }),
]);

export type AuditRecommendationOutcome = z.infer<
  typeof AuditRecommendationOutcomeSchema
>;
